import { observer } from "mobx-react";
import React from "react";
import { intl } from "@fidget/common/i18n/TypedIntl";
import commonStyles from "../../../containers/settings/settingsEditCommon.css";
import { useEventActionsSettings } from "../bots/useEventActionsSettings";
import { DeleteConfirmationToastOnError } from "../settingsForms/deleteConfirmation/deleteConfirmation";
import { ElementFormActions } from "../settingsForms/elementForm/actions/elementFormActions";
import { ElementFormContent } from "../settingsForms/elementForm/content/elementFormContent";
import { ElementForm } from "../settingsForms/elementForm/elementForm/elementForm";
import { ElementFormDeleteIcon } from "../settingsForms/elementForm/icons/elementFormDeleteIcon";
import { ElementFormEditIcon } from "../settingsForms/elementForm/icons/elementFormEditIcon";
import { ElementFormSaveDiscardIcon } from "../settingsForms/elementForm/icons/elementFormSaveDiscardIcon";
import { FormPlaceholder } from "../settingsForms/fullWidthPlaceholder/formPlaceholder";
import { VerticalSeparator } from "../settingsForms/separators/verticalSeparator/verticalSeparator";
import { SubtitleDescription } from "../subtitleDescription";
import { ExistingEventActionBox } from "./components/existingEventActionBox/existingEventActionBox";
import { NewEventActionBox } from "./components/newEventActionBox/newEventActionBox";
import { ExistingActionFormType, NewActionFormType } from "./types";

export const EventActionsBotSettings = observer(() => {
  const {
    saveNewItem,
    newItem,
    discardNewItem,
    handleCreateNewClick,

    items,
    currentlyEditedItem,
    onExistingEditClick,

    uiRemoveExistingItem,
    apiDeleteItem,
    discardEditedItem,
    saveEditedItemChangesLocally,
    currentLanguage
  } = useEventActionsSettings();

  const renderNewActionForm = (form: NewActionFormType) => {
    return (
      <ElementForm isEdited>
        <ElementFormContent>
          <NewEventActionBox currentLanguage={currentLanguage} form={form} />
        </ElementFormContent>
        <VerticalSeparator />
        <ElementFormActions>
          <ElementFormSaveDiscardIcon onSave={saveNewItem} onDiscard={discardNewItem} />
        </ElementFormActions>
      </ElementForm>
    );
  };

  const renderAddNewPlaceholder = () => (
    <FormPlaceholder
      onClick={handleCreateNewClick}
      text={intl.formatMessage("settings.eventActions.addNew")}
    />
  );

  const renderNewActionComponent = () =>
    newItem ? renderNewActionForm(newItem) : renderAddNewPlaceholder();

  const renderElementForm = (item: ExistingActionFormType, onDeleteClick: () => void) => {
    const isCurrentlyEdited = !!currentlyEditedItem && item.$.id.$ === currentlyEditedItem.$.id.$;
    const currentForm = isCurrentlyEdited && currentlyEditedItem ? currentlyEditedItem : item;

    return (
      <ElementForm isEdited={isCurrentlyEdited}>
        <ElementFormContent>
          {isCurrentlyEdited ? (
            <NewEventActionBox
              currentLanguage={currentLanguage}
              id={item.$.id.$}
              form={currentForm}
            />
          ) : (
            <ExistingEventActionBox form={currentForm} />
          )}
        </ElementFormContent>
        <VerticalSeparator />
        {isCurrentlyEdited ? (
          <ElementFormActions>
            <ElementFormSaveDiscardIcon
              onSave={saveEditedItemChangesLocally}
              onDiscard={discardEditedItem}
            />
          </ElementFormActions>
        ) : (
          <ElementFormActions>
            <ElementFormEditIcon onClick={() => onExistingEditClick(item)} />
            <ElementFormDeleteIcon onClick={onDeleteClick} />
          </ElementFormActions>
        )}
      </ElementForm>
    );
  };

  const renderExistingList = () => {
    return items
      .filter((item) => item.$.locale.$ === currentLanguage)
      .map((item) => {
        return (
          <DeleteConfirmationToastOnError
            key={item.$.id.$}
            areYouSureText={intl.formatMessage("settings.proactiveMessage.areYouSureToRemove")}
            succesfullyDeletedText={intl.formatMessage("settings.eventActions.deleteConfirmation")}
            onDelete={() => apiDeleteItem(item.$.id.$)}
            onTimeoutPassed={() => uiRemoveExistingItem(item.$.id.$)}
            renderComponent={(onDeleteClick) => renderElementForm(item, onDeleteClick)}
          />
        );
      });
  };

  return (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.eventActions.list")}
        description={intl.formatMessage("settings.eventActions.description")}
      />
      <div className={commonStyles.content}>
        {renderNewActionComponent()}
        {renderExistingList()}
      </div>
    </div>
  );
});
